/**
 * Panneau d'un POI public de l'organisation : nom, type, site web ; le convertir en équipement
 * (par exemple pour le rendre privé), le supprimer. Un POI est toujours public.
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { POICategory } from '@web/features/map/services/mapApi'
import type { OrgPoi } from '../../api'
import { KindSwitch } from './KindSwitch'

type Props = {
  poi: OrgPoi
  categories: POICategory[]
  busy: boolean
  onBack: () => void
  onSave: (data: { name: string; poi_type_id: number; website: string | null; osm_sync_requested: boolean }) => void
  onConvert: () => void
  onDelete: () => void
}

export function PoiPanel({ poi, categories, busy, onBack, onSave, onConvert, onDelete }: Props) {
  const { t } = useTranslation('gestion')
  const [name, setName] = useState(poi.name)
  const [typeId, setTypeId] = useState<number | null>(poi.poi_type_id)
  const [website, setWebsite] = useState(poi.website ?? '')
  const [osmSync, setOsmSync] = useState(poi.osm_sync_requested)
  useEffect(() => {
    setName(poi.name)
    setTypeId(poi.poi_type_id)
    setWebsite(poi.website ?? '')
    setOsmSync(poi.osm_sync_requested)
  }, [poi])
  const dirty =
    name !== poi.name || typeId !== poi.poi_type_id || website !== (poi.website ?? '') || osmSync !== poi.osm_sync_requested

  return (
    <div className="g-editor__details">
      <button type="button" className="g-back" onClick={onBack}>
        <span className="mdi mdi-arrow-left" aria-hidden /> {t('carte.retour_liste')}
      </button>
      <div>
        <span className="g-badge g-badge--info">{t('carte.poi_public')}</span>
      </div>
      <h2 className="g-editor__section">{poi.name}</h2>
      {poi.poi_type && <p className="g-muted g-poi__type">{poi.poi_type.name}</p>}
      <KindSwitch value="poi" disabled={busy} onChange={onConvert} />
      <label className="g-field">
        <span className="g-field__label">{t('carte.type')}</span>
        <select className="g-input" value={typeId ?? ''} onChange={(e) => setTypeId(Number(e.target.value))}>
          {categories.map((c) => (
            <optgroup key={c.id} label={c.name}>
              {(c.children ?? []).map((child) => (
                <option key={child.id} value={child.id}>
                  {child.name}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </label>
      <label className="g-field">
        <span className="g-field__label">{t('carte.nom')}</span>
        <input className="g-input" value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <label className="g-field">
        <span className="g-field__label">{t('carte.site_web')}</span>
        <input className="g-input" type="url" value={website} placeholder="https://" onChange={(e) => setWebsite(e.target.value)} />
      </label>
      <label className="g-check">
        <input type="checkbox" checked={osmSync} onChange={(e) => setOsmSync(e.target.checked)} />
        <span>
          <strong>{t('carte.synchro_osm')}</strong>
          <span className="g-hint">
            {poi.osm_id ? t('carte.aide_synchro_osm_existant', { id: poi.osm_id }) : t('carte.aide_synchro_osm')}
          </span>
        </span>
      </label>
      <p className="g-hint">{t('carte.aide_poi_public')}</p>
      <div className="g-editor__buttons">
        <button
          type="button"
          className="btn btn--primary"
          disabled={busy || !dirty || !name.trim() || !typeId}
          onClick={() => typeId && onSave({ name: name.trim(), poi_type_id: typeId, website: website.trim() || null, osm_sync_requested: osmSync })}
        >
          {busy ? t('commun.enregistrement') : t('commun.enregistrer')}
        </button>
        <span className="g-spacer" />
        <button type="button" className="btn g-danger" disabled={busy} onClick={onDelete}>
          <span className="mdi mdi-trash-can-outline" aria-hidden /> {t('commun.supprimer')}
        </button>
      </div>
    </div>
  )
}
